import api from '../services/axios';
import { resolveLandingPath } from './roleAccess';

const SESSION_ENDED_KEY = 'reachout_session_ended';
/** Survives new tabs. sessionStorage alone was why logout still auto-opened the previous user. */
const LOGGED_OUT_KEY = 'reachout_logged_out';
const SSO_PENDING_KEY = 'reachout_sso_pending';
const LAST_PROTECTED_PATH_KEY = 'reachout_last_path';
const BRIDGE_ATTEMPTED_KEY = 'reachout_wizetok_bridge_attempted';
const CROSS_APP_SSO_KEY = 'reachout_cross_app_sso';
const AUTH_EMAIL_KEY = 'reachout_auth_email';
const FRESH_LOGIN_KEY = 'reachout_fresh_login';
const TENANT_SELECTED_KEY = 'reachout_tenant_selected';

export function sessionWhoEmail() {
  try {
    const match = document.cookie.match(/(?:^|; )hx_who=([^;]*)/);
    if (!match) return '';
    return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim().toLowerCase();
  } catch {
    return '';
  }
}

export function pinAuthEmail(email) {
  const value = String(email || '').trim().toLowerCase();
  if (!value) return;
  setKey(sessionStorage, AUTH_EMAIL_KEY, value);
}

export function pinnedAuthEmail() {
  try {
    return (sessionStorage.getItem(AUTH_EMAIL_KEY) || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export function clearPinnedAuthEmail() {
  clearKey(sessionStorage, AUTH_EMAIL_KEY);
}

export function markFreshLogin() {
  setKey(sessionStorage, FRESH_LOGIN_KEY, '1');
  clearPinnedAuthEmail();
  clearTenantSelection();
}

export function isFreshLogin() {
  try {
    return sessionStorage.getItem(FRESH_LOGIN_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearFreshLogin() {
  clearKey(sessionStorage, FRESH_LOGIN_KEY);
}

export function confirmTenantSelection(tenantId) {
  if (tenantId == null || tenantId === '') return;
  setKey(sessionStorage, TENANT_SELECTED_KEY, String(tenantId));
}

export function isTenantSelectionConfirmed(tenantId) {
  if (tenantId == null || tenantId === '') return false;
  try {
    return sessionStorage.getItem(TENANT_SELECTED_KEY) === String(tenantId);
  } catch {
    return false;
  }
}

export function clearTenantSelection() {
  clearKey(sessionStorage, TENANT_SELECTED_KEY);
}

export function sessionMatchesPinnedUser(email) {
  const pin = pinnedAuthEmail();
  const got = String(email || '').trim().toLowerCase();
  if (!pin || !got) return true;
  return pin === got;
}

export function markSsoAuthPending() {
  try {
    sessionStorage.setItem(SSO_PENDING_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearSsoAuthPending() {
  try {
    sessionStorage.removeItem(SSO_PENDING_KEY);
  } catch {
    // ignore
  }
}

export function isSsoAuthPending() {
  try {
    return sessionStorage.getItem(SSO_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export const PROTECTED_ROUTE_PREFIXES = [
  '/open-positions',
  '/existing-positions',
  '/database',
  '/jd-preview',
  '/stack-ranking',
  '/resume-formatter',
  '/engagement',
  '/analytics',
  '/admin',
  '/set-password',
];

export function isProtectedPathname(pathname) {
  const path = String(pathname || '');
  return PROTECTED_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function clearKey(store, key) {
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
}

function setKey(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    // ignore
  }
}

let lastProtectedShellPath = '';

/** Set when the user explicitly logs out (survives reload, new tab, and bfcache). */
export function markSessionEnded() {
  const now = String(Date.now());
  lastProtectedShellPath = '';
  setKey(sessionStorage, SESSION_ENDED_KEY, now);
  setKey(localStorage, LOGGED_OUT_KEY, now);
  clearKey(sessionStorage, LAST_PROTECTED_PATH_KEY);
  clearKey(sessionStorage, SSO_PENDING_KEY);
  clearKey(sessionStorage, BRIDGE_ATTEMPTED_KEY);
  clearKey(sessionStorage, CROSS_APP_SSO_KEY);
  clearKey(sessionStorage, 'reachout_return_to');
  clearKey(sessionStorage, AUTH_EMAIL_KEY);
  clearKey(sessionStorage, FRESH_LOGIN_KEY);
  clearKey(sessionStorage, TENANT_SELECTED_KEY);
}

export function clearSessionEndedMarker() {
  clearKey(sessionStorage, SESSION_ENDED_KEY);
  clearKey(localStorage, LOGGED_OUT_KEY);
}

export function isSessionEndedLocally() {
  try {
    return sessionStorage.getItem(SESSION_ENDED_KEY) != null
      || localStorage.getItem(LOGGED_OUT_KEY) != null;
  } catch {
    return false;
  }
}

/** Hard redirect when a protected URL is opened after logout (sync, no React required). */
export function blockProtectedRouteIfLoggedOut() {
  if (!isSessionEndedLocally()) return false;
  const path = window.location.pathname || '';
  if (!isProtectedPathname(path)) return false;
  try {
    window.location.replace('/login');
  } catch {
    window.location.href = '/login';
  }
  return true;
}

/**
 * Runs before React on every load / bfcache restore / history navigation.
 * Prevents the back button from showing a cached authenticated screen after logout.
 */
export function installAuthNavigationGuards() {
  if (typeof window === 'undefined') return;
  if (window.__reachoutAuthGuardsInstalled) return;
  window.__reachoutAuthGuardsInstalled = true;

  const guard = () => {
    blockProtectedRouteIfLoggedOut();
  };

  window.addEventListener('pageshow', guard);
  window.addEventListener('popstate', guard);
}

/** On the login page, trap back so it does not return to cached protected routes. */
export function trapLoginPageHistory() {
  if (typeof window === 'undefined' || !isSessionEndedLocally()) return undefined;

  try {
    window.history.replaceState({ reachoutLoginTrap: true }, '', '/login');
    window.history.pushState({ reachoutLoginTrap: true }, '', '/login');
  } catch {
    // ignore
  }

  const onPopState = () => {
    try {
      window.history.pushState({ reachoutLoginTrap: true }, '', '/login');
    } catch {
      blockProtectedRouteIfLoggedOut();
    }
  };

  window.addEventListener('popstate', onPopState);
  return () => window.removeEventListener('popstate', onPopState);
}

/**
 * Returns true when the server accepts the current session cookie.
 * @param {{ ignoreEndedMarker?: boolean }} [options]
 *   After Microsoft SSO the logout marker can still be set even though
 *   cookies were written. Pass ignoreEndedMarker to trust /auth/me.
 */
export async function checkAuthSession(options = {}) {
  if (!options.ignoreEndedMarker && isSessionEndedLocally()) return false;
  try {
    const res = await api.get('/auth/me', {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    const email = res.data?.email;
    if (email) {
      pinAuthEmail(email);
    }
    clearSessionEndedMarker();
    return true;
  } catch {
    return false;
  }
}

export async function postLoginTarget() {
  try {
    const res = await api.get('/auth/me', {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    const lastPath = getProtectedShellPath();
    const candidate =
      lastPath && isProtectedPathname(String(lastPath).split('?')[0]) ? lastPath : '';
    return resolveLandingPath(res.data, candidate);
  } catch {
    return '/open-positions';
  }
}

/** Full-page redirect to login after explicit logout. */
export function redirectToLogin() {
  markSessionEnded();
  try {
    window.history.replaceState({ reachoutLoggedOut: true }, '', '/login');
  } catch {
    // ignore
  }
  try {
    window.location.replace('/login');
  } catch {
    window.location.href = '/login';
  }
}

/** Discourage bfcache while viewing protected content (best-effort). */
export function installBfCacheBlocker() {
  if (typeof window === 'undefined') return undefined;
  const noop = () => {};
  const onPageHide = (event) => {
    if (event.persisted) {
      blockProtectedRouteIfLoggedOut();
    }
  };
  window.addEventListener('unload', noop);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    window.removeEventListener('unload', noop);
    window.removeEventListener('pagehide', onPageHide);
  };
}

function readStoredProtectedPath() {
  try {
    const stored = sessionStorage.getItem(LAST_PROTECTED_PATH_KEY);
    if (stored && isProtectedPathname(stored.split('?')[0])) return stored;
  } catch {
    // ignore
  }
  return '';
}

function writeStoredProtectedPath(path) {
  if (typeof path !== 'string' || !path) return;
  lastProtectedShellPath = path;
  try {
    sessionStorage.setItem(LAST_PROTECTED_PATH_KEY, path);
  } catch {
    // ignore
  }
}

/** Updated by ProtectedLayout on each in-app navigation. */
export function setProtectedShellPath(path) {
  if (typeof path === 'string' && path) {
    writeStoredProtectedPath(path);
  }
}

export function clearProtectedShellPath() {
  lastProtectedShellPath = '';
  clearKey(sessionStorage, LAST_PROTECTED_PATH_KEY);
}

/** Last in-app URL to restore after Chrome trackpad back / browser back. */
export function getProtectedShellPath() {
  return lastProtectedShellPath || readStoredProtectedPath();
}

function restoreProtectedShell() {
  const stay = getProtectedShellPath();
  if (!stay) return false;
  if (!isProtectedPathname(stay.split('?')[0])) return false;

  const current = `${window.location.pathname}${window.location.search}`;
  if (current === stay) return false;

  try {
    window.history.pushState({ reachoutAppShell: true }, '', stay);
  } catch {
    // ignore
  }
  window.location.replace(stay);
  return true;
}

// Paths where restoreProtectedShell must never fire — navigating away mid-flow
// aborts the in-flight exchange-token fetch (Caddy/nginx logs 499) and breaks SSO.
const AUTH_FLOW_PATHS = ['/auth/callback', '/auth/success', '/auth/failure', '/unauthorized'];

function isAuthFlowPathname(pathname) {
  return AUTH_FLOW_PATHS.some((p) => (pathname || '').startsWith(p));
}

/**
 * Global back/swipe guard — runs in capture phase before React Router.
 * Chrome trackpad swipe triggers history.back() to /login; Edge often ignores it.
 */
export function installProtectedShellHistoryGuard() {
  if (typeof window === 'undefined' || window.__reachoutProtectedShellGuard) return;
  window.__reachoutProtectedShellGuard = true;

  if (isProtectedPathname(window.location.pathname)) {
    writeStoredProtectedPath(`${window.location.pathname}${window.location.search}`);
  }

  const onPopState = () => {
    if (isSessionEndedLocally()) return;

    const pathname = window.location.pathname;
    const current = `${pathname}${window.location.search}`;

    if (isAuthFlowPathname(pathname)) return;

    if (isProtectedPathname(pathname)) {
      writeStoredProtectedPath(current);
      return;
    }

    restoreProtectedShell();
  };

  const onPageShow = (event) => {
    if (isSessionEndedLocally()) return;
    const pathname = window.location.pathname;
    if (isAuthFlowPathname(pathname)) return;
    if (!isProtectedPathname(pathname)) {
      restoreProtectedShell();
    } else if (event.persisted) {
      writeStoredProtectedPath(`${pathname}${window.location.search}`);
    }
  };

  window.addEventListener('popstate', onPopState, true);
  window.addEventListener('pageshow', onPageShow);
}

installAuthNavigationGuards();
installProtectedShellHistoryGuard();

if (typeof window !== 'undefined') {
  blockProtectedRouteIfLoggedOut();
}
